// Display-only parser. Extracts the tool calls + SEARCH/REPLACE edit
// blocks the LLM embedded inline in an assistant message and returns
// them as structured "actions" alongside the leftover prose.
//
// Why on the FRONTEND too:
//   The server-side orchestrator parses these and dispatches them
//   (lib/tool_parser.ts + lib/edit_parser.ts). That's the source of
//   truth for behaviour. But the assistant message TEXT we persist
//   to the thread still contains the original `tool` fences and
//   SEARCH/REPLACE blocks verbatim — rendering them raw would dump
//   JSON in the chat. The frontend re-parses the same shapes purely
//   to STRIP them from the prose + show them as compact chips.
//
// Contract:
//   extractActions(text) → { prose, actions }
//     - prose: the assistant's natural-language reply with all
//       action blocks removed. Newlines collapsed where blocks were.
//     - actions: ordered list of what the LLM emitted (tool calls
//       and edits, interleaved as they appeared in the source).
//
// Tolerance:
//   - Malformed tool JSON → block is REMOVED from prose, NO chip
//     emitted (we don't want a "syntax error" chip in chat — that
//     UX failure is the orchestrator's domain via parse-error
//     feedback to the LLM).
//   - SEARCH/REPLACE blocks recognized only when the line directly
//     above the `<<<<<<< SEARCH` fence carries the path (same
//     convention as the server parser).

export type ToolAction = { kind: "tool"; name: string; input: Record<string, any> };
export type EditAction = { kind: "edit"; path: string };
export type Action     = ToolAction | EditAction;

// Variable-length tool fence: opener is N backticks (>= 3) + "tool"
// + optional trailing whitespace + newline. Closer is the SAME N
// backticks (backref \1) plus optionally MORE backticks (CommonMark
// allows the closer to be longer than the opener). This mirrors the
// server's lib/tool_parser.ts contract so the chat strip stays in
// sync with what the orchestrator actually dispatched.
const RE_TOOL_FENCE     = /(`{3,})tool[ \t]*\n([\s\S]*?)\n\1`*[ \t]*(?=\n|$)/g;
const RE_EDIT_BLOCK     = /(^|\n)([^\n]+)\n<{7} SEARCH\s*\n[\s\S]*?\n={7}\s*\n[\s\S]*?\n>{7} REPLACE\s*(?=\n|$)/g;

export function extractActions(text: string): { prose: string; actions: Action[] } {
  const actions: Action[] = [];

  // 1. Strip + capture tool-fenced JSON. Replace with "" so the prose
  // collapses naturally.
  let prose = text.replace(RE_TOOL_FENCE, (_match, _backticks: string, body: string) => {
    try {
      const obj = JSON.parse(body.trim());
      if (obj && typeof obj === "object" && typeof (obj as any).name === "string") {
        actions.push({
          kind:  "tool",
          name:  String((obj as any).name),
          input: (obj as any).input && typeof (obj as any).input === "object" ? (obj as any).input : {},
        });
      }
    } catch { /* malformed — drop from prose, no chip */ }
    return "";
  });

  // 2. Strip + capture SEARCH/REPLACE blocks. The captured path is
  // the line directly above the SEARCH fence.
  prose = prose.replace(RE_EDIT_BLOCK, (_match, _leading, path: string) => {
    const trimmed = path.trim();
    if (trimmed) actions.push({ kind: "edit", path: trimmed });
    return "";
  });

  // 3. Tidy up the prose — collapse 3+ consecutive newlines into 2
  // (single blank line). Trim leading/trailing whitespace.
  prose = prose.replace(/\n{3,}/g, "\n\n").trim();

  return { prose, actions };
}

// Build mode is silent in the chat — prose is suppressed and only
// chips render. `ask` is the one EXCEPTION: the model uses it
// specifically to interrupt the silent build flow and surface a
// plain-language question to the operator, so its `question` text
// MUST render as a visible bubble (a chip labelled "ask" would hide
// the actual question and leave the operator looking at a flickering
// indicator with nothing to read).
//
// Returns the question string when the actions list contains a valid
// `ask` tool call with a non-empty `question` input, else null. Pure.
export function extractAskQuestion(actions: ReadonlyArray<Action>): string | null {
  const ask = actions.find((a): a is ToolAction => a.kind === "tool" && a.name === "ask");
  if (!ask) return null;
  const q = (ask.input as { question?: unknown }).question;
  return typeof q === "string" && q.trim().length > 0 ? q : null;
}

// Format ONE action as a chip label. Pure function — kept here so the
// rendering layer has a single source of truth for the wording.
export function actionLabel(a: Action): string {
  if (a.kind === "edit") return `edit ${a.path}`;
  // tool
  const { name, input } = a;
  switch (name) {
    case "view":       return `view ${input?.path ?? ""}`.trim();
    case "list_files": return "list files";
    case "create":     return `create ${input?.path ?? ""}`.trim();
    case "delete":     return `delete ${input?.path ?? ""}`.trim();
    case "deploy_dev": return "deploy";
    case "call_dev":   return `call ${input?.method ?? ""} ${input?.path ?? ""}`.trim();
    case "run_test":   return `test ${input?.entry ?? ""}`.trim();
    case "events":     return "events";
    case "done":       return `done${input?.ok === false ? " (failed)" : ""}`;
    default:           return name;
  }
}
